import { useEffect, useState } from "react";
import { uploadDocument } from "../api/documents";
import { getMyInmateAccess } from "../api/inmateAccess";
import AttorneyNav from "../components/AttorneyNav";
import AsyncButton from "../components/AsyncButton";
import StatusMessage from "../components/StatusMessage";
import { getErrorMessage } from "../utils/apiErrors";
import { DEFAULT_DOCUMENT_POLICY, validateDocumentSelection } from "../utils/documentPolicy";
import { getRegistrationConfiguration } from "../api/registration";

export default function AttorneyUpload() {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [clientAccessId, setClientAccessId] = useState("");
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [policy, setPolicy] = useState(DEFAULT_DOCUMENT_POLICY);

  const selectedClient = clients.find(
    (record) => String(record.id) === String(clientAccessId)
  );

  const trustedTunnelEnabled =
    selectedClient?.facility?.attorney_upload_mode === "trusted_tunnel";

  const loadClients = async () => {
    try {
      const res = await getMyInmateAccess();

      setClients(
        (res.data.data || []).filter((record) => record.status === "approved")
      );
    } catch (err) {
      setError(getErrorMessage(err, "Unable to load approved clients."));
    }
  };

  useEffect(() => {
    loadClients();
    getRegistrationConfiguration().then((response) => setPolicy({ ...DEFAULT_DOCUMENT_POLICY, ...(response.data?.document_policy || {}) })).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file || !title || !clientAccessId) {
      setError("Select an approved client, enter a title, and choose a PDF.");
      return;
    }

    if (loading) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("title", title);
    formData.append("attorney_inmate_access_id", clientAccessId);

    setLoading(true);
    setError("");
    setMessage("");
    try {
      const response = await uploadDocument(formData);
      setMessage(response?.data?.message || "Document uploaded successfully.");

      setFile(null);
      setTitle("");
      setClientAccessId("");
    } catch (err) {
      setError(getErrorMessage(err, "Unable to upload the document."));
    } finally {
      setLoading(false);
    }
  };

  const chooseFile = (event) => {
    const selected = event.target.files[0] || null;
    const selectionError = validateDocumentSelection(selected, policy);
    if (selectionError) { setFile(null); setError(selectionError); event.target.value = ''; return; }
    setError(''); setFile(selected);
  };

  return (
    <div className="app-shell">
      <div className="topbar d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-3">
          <div className="brand-mark">CV</div>
          <div>
            <h5 className="mb-0">CounselVault</h5>
            <div className="small opacity-75">Attorney Document Submission</div>
          </div>
        </div>
        <AttorneyNav />
      </div>

      <main className="page-wrap">
        <StatusMessage type="success">{message}</StatusMessage>
        <StatusMessage type="warning">{error}</StatusMessage>
        <div className="mb-4">
          <h2 className="mb-1">Upload Legal Document</h2>
          <p className="text-muted mb-0">
            Submit privileged legal documents only for facility-approved clients.
          </p>
        </div>

        <div className="row g-4">
          <div className="col-lg-7">
            <div className="portal-card">
              <div className="portal-card-header">
                <h5 className="mb-0">New Submission</h5>
                <div className="text-muted small">
                  Attorneys may only upload for approved inmate/client relationships.
                </div>
              </div>

              <div className="portal-card-body">
                {clients.length === 0 ? (
                  <div className="text-center py-5">
                    <h5>No approved clients yet</h5>
                    <p className="text-muted mb-4">
                      Request client access before uploading documents.
                    </p>

                    <button
                      className="btn btn-dark"
                      onClick={() => {
                        window.location.href = "/attorney/inmate-access";
                      }}
                    >
                      Request Client Access
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                      <label className="form-label" htmlFor="approved-client">Approved Client / Inmate</label>
                      <select
                        id="approved-client"
                        className="form-select form-select-lg"
                        value={clientAccessId}
                        onChange={(e) => setClientAccessId(e.target.value)}
                      >
                        <option value="">Select approved client</option>
                        {clients.map((record) => (
                          <option key={record.id} value={record.id}>
                            {record.inmate?.first_name} {record.inmate?.last_name}
                            {" — "}
                            #{record.inmate?.inmate_number}
                            {" — "}
                            {record.facility?.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="mb-3">
                      <label className="form-label" htmlFor="document-title">Document Title</label>
                      <input
                        id="document-title"
                        className="form-control form-control-lg"
                        placeholder="Example: Motion for Review"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                      />
                    </div>

                    <div className="mb-4">
                      <label className="form-label" htmlFor="document-file">PDF File</label>
                      <input
                        id="document-file"
                        type="file"
                        className="form-control form-control-lg"
                        accept="application/pdf"
                        onChange={chooseFile}
                        aria-describedby="document-file-help"
                      />
                      <div className="form-text" id="document-file-help">
                        <strong>{policy.disclosure}</strong>{' '}
                        {policy.security_review_enforced ? (trustedTunnelEnabled
                          ? "Security scanning occurs first. If it succeeds, Trusted Tunnel delivery may release the document automatically."
                          : "Security scanning occurs first. Facility review becomes available after it succeeds.")
                          : (trustedTunnelEnabled ? "Trusted Tunnel delivery is enabled for this facility." : "Document will enter facility review before release.")}
                      </div>
                    </div>

                    <AsyncButton className="btn btn-dark btn-lg w-100" busy={loading} busyText="Submitting securely…">
                      {trustedTunnelEnabled ? "Upload for Secure Delivery" : "Submit for Review"}
                    </AsyncButton>
                  </form>
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-5">
            <div className="portal-card h-100">
              <div className="portal-card-header">
                <h5 className="mb-0">Secure Legal Mail Workflow</h5>
              </div>

              <div className="portal-card-body">
                <div className="d-flex gap-3 mb-4">
                  <span className="status-pill status-approved">1</span>
                  <div>
                    <strong>Facility Access</strong>
                    <p className="text-muted mb-0">
                      Facility must approve the attorney.
                    </p>
                  </div>
                </div>

                <div className="d-flex gap-3 mb-4">
                  <span className="status-pill status-approved">2</span>
                  <div>
                    <strong>Client Access</strong>
                    <p className="text-muted mb-0">
                      Facility must approve the attorney-client/inmate relationship.
                    </p>
                  </div>
                </div>

                <div className="d-flex gap-3 mb-4">
                  <span className="status-pill status-submitted">3</span>
                  <div>
                    <strong>
                      {trustedTunnelEnabled
                        ? "Trusted Tunnel Delivery"
                        : "Document Review"}
                    </strong>
                    <p className="text-muted mb-0">
                      {trustedTunnelEnabled
                        ? (policy.security_review_enforced ? "Facility has enabled automatic delivery after security scanning succeeds." : "Facility has enabled direct attorney-to-inmate delivery.")
                        : "Facility reviews and controls release."}
                    </p>
                  </div>
                </div>

                <div className="d-flex gap-3">
                  <span className="status-pill status-released">4</span>
                  <div>
                    <strong>Inmate Access</strong>
                    <p className="text-muted mb-0">
                      {trustedTunnelEnabled
                        ? (policy.security_review_enforced ? "Inmate access begins only after security scanning succeeds and delivery completes." : "Inmate can view the document after trusted delivery completes.")
                        : "Inmate sees only released documents."}
                    </p>
                  </div>
                </div>

                <hr />

                <p className="text-muted small mb-0">
                  This workflow avoids roster browsing and keeps every action auditable.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
